import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useSocket } from '../../contexts/SocketContext';
import TabBar from '../../components/TabBar';
import { BarChart3, ArrowLeft, CheckCircle2, Layers, Hash, Phone } from 'lucide-react';
import api from '../../utils/api';
import AdminNotificationBell from '../../components/AdminNotificationBell';

// 派发详情：按接单人（含外部档案）聚合已接批数、完成批数、总条数，降序排列
// 含管理员/代理后台代接的记录（task_batches 内 submitted_by_role IN ('admin','agent') 同样计入）
export default function DispatchDetailsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { subscribe } = useSocket();
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDetails = useCallback(async () => {
    try {
      const res = await api.get('/dispatch/details');
      setDetails(res.data.details || []);
    } catch (err) {
      showToast(err.response?.data?.error || '加载派发详情失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  // 接量/完成/结算变化时实时刷新
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'data_refresh' || event === 'stats_refresh' || event === 'settlement_updated' || event === 'users_updated') {
        fetchDetails();
      }
    });
  }, [subscribe, fetchDetails]);

  const displayName = (u) => u.name || u.username || (String(u.phone).startsWith('ext') ? '' : u.phone) || `用户${u.id}`;
  const totalCount = details.reduce((s, d) => s + (d.total_count || 0), 0);
  const totalBatches = details.reduce((s, d) => s + (d.received_batches || 0), 0);

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}
        >
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChart3 size={16} /> 派发详情
          </span>
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* 汇总 */}
        <div className="card" style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, background: 'var(--c-primary-light)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--c-primary)' }}>{details.length}</div>
            <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>接单人</div>
          </div>
          <div style={{ flex: 1, background: 'var(--c-bg)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--c-text)' }}>{totalBatches}</div>
            <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>总批数</div>
          </div>
          <div style={{ flex: 1, background: 'rgba(52,199,89,0.08)', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--c-success)' }}>{totalCount}</div>
            <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>总条数</div>
          </div>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : details.length === 0 ? (
          <div className="card empty-state">
            <div className="empty-icon"><BarChart3 size={40} color="var(--c-text-secondary)" strokeWidth={1.2} /></div>
            <p>暂无派发记录</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {details.map(u => {
              const isExternal = u.is_external === 1;
              return (
                <div key={u.id} className="card" style={{ padding: '12px 14px' }} onClick={() => navigate(`/admin/dispatch-view/${u.id}`)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {displayName(u)}
                        {isExternal && <span className="badge badge-warning" style={{ fontSize: 10 }}>外部</span>}
                        {u.role === 'agent' && <span className="badge badge-info" style={{ fontSize: 10 }}>代理</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        {u.username && (
                          <span><Hash size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} />{u.username}</span>
                        )}
                        {u.phone && !String(u.phone).startsWith('ext') && (
                          <span><Phone size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} />{u.phone}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--c-primary)' }}>{u.total_count || 0}</div>
                      <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginTop: 1 }}>条</div>
                    </div>
                  </div>

                  {/* 批次统计 */}
                  <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 13, color: 'var(--c-text-secondary)', alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Layers size={13} color="var(--c-primary)" />
                      已接 <strong style={{ color: 'var(--c-text)' }}>{u.received_batches || 0}</strong> 批
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <CheckCircle2 size={13} color="var(--c-success)" />
                      完成 <strong style={{ color: 'var(--c-success)' }}>{u.completed_batches || 0}</strong> 批
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <TabBar mode="admin" />
    </div>
  );
}
